import type { Router } from 'vue-router';
import { consent, whenConsented } from './consent';
import { isNativeApp } from './native';

/**
 * Google Analytics 4, chargé SEULEMENT après acceptation de la mesure
 * d'audience (whenConsented) — jamais par une balise <script> directe.
 * Inactif sans identifiant de mesure, dans les applis mobiles, et sur les
 * pages privées : on ne mesure que le site public (acquisition), jamais
 * l'usage de l'espace client.
 */
/** Identifiant de flux GA4 (public : il figure dans toutes les pages). Vide = désactivé. */
const MEASUREMENT_ID: string = 'G-G6N2W2J1VC';

type Gtag = (...args: unknown[]) => void;
declare global {
  interface Window {
    dataLayer?: unknown[];
    gtag?: Gtag;
  }
}

let ready = false;

function load(id: string): void {
  window.dataLayer = window.dataLayer ?? [];
  // gtag attend l'objet `arguments` lui-même, pas un tableau.
  window.gtag = function gtag() {
    // eslint-disable-next-line prefer-rest-params
    window.dataLayer!.push(arguments);
  };
  window.gtag('consent', 'default', { analytics_storage: 'granted', ad_storage: 'denied', ad_user_data: 'denied', ad_personalization: 'denied' });
  window.gtag('js', new Date());
  // Pages vues envoyées à la main : le SPA change d'URL sans recharger.
  window.gtag('config', id, { send_page_view: false, allow_google_signals: false, allow_ad_personalization_signals: false });
  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${encodeURIComponent(id)}`;
  document.head.appendChild(script);
  ready = true;
}

function pageView(path: string): void {
  if (!ready || !window.gtag) return;
  window.gtag('event', 'page_view', { page_path: path, page_location: `${window.location.origin}${path}`, page_title: document.title });
}

/**
 * Retrait ou refus du consentement : les cookies de mesure déjà déposés sont
 * effacés (le rechargement de consent.ts arrête le script, pas ses cookies).
 * GA les pose sur le domaine racine (`.yakafa.fr`) : on efface sur les deux.
 */
export function clearAnalyticsCookies(): void {
  const names = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0]!.trim())
    .filter((name) => name === '_ga' || name.startsWith('_ga_') || name === '_gid');
  const host = window.location.hostname;
  const root = host.split('.').slice(-2).join('.');
  for (const name of names) {
    for (const domain of ['', `; domain=${host}`, `; domain=.${root}`]) {
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`;
    }
  }
}

export function setupAnalytics(router: Router): void {
  if (!MEASUREMENT_ID || isNativeApp) return;
  if (!consent.value.analytics) clearAnalyticsCookies();
  const id = MEASUREMENT_ID;
  const isPublic = () => Boolean(router.currentRoute.value.meta.seo);
  whenConsented('analytics', () => {
    load(id);
    if (isPublic()) pageView(router.currentRoute.value.path);
  });
  router.afterEach((to) => {
    if (to.meta.seo) pageView(to.path);
  });
}
